import { useEffect, useRef, useState } from 'react'
import { useI18n } from '../i18n/I18nProvider'
import { useApp } from '../store/AppProvider'
import { api } from '../lib/api'

/* ------------------------------------------------------------------ icon --- */
/**
 * Ikon yang MENUNJUK ARAH dan karena itu harus ditukar saat halaman jadi RTL.
 *
 * Ditangani di sini, bukan di tiap pemanggil, karena ini jenis kesalahan yang
 * pasti kembali: siapa pun yang menambah tombol "lanjut" berikutnya akan
 * menulis `arrow_forward` begitu saja — dan panah itu akan menunjuk mundur bagi
 * pembaca Arab tanpa ada yang menyadarinya. Dengan penukaran terpusat, kode
 * pemanggil boleh selalu menulis arah versi LTR dan tetap benar.
 *
 * Yang TIDAK masuk daftar: ikon vertikal (expand_more), ikon yang bentuknya
 * kebetulan miring tapi bukan arah (trending_up), dan ikon yang mengacu pada
 * benda nyata (send — pesawat kertas, dibiarkan karena membaliknya justru aneh).
 */
const MIRROR = {
  arrow_forward: 'arrow_back', arrow_back: 'arrow_forward',
  chevron_right: 'chevron_left', chevron_left: 'chevron_right',
  arrow_right_alt: 'arrow_left_alt', arrow_left_alt: 'arrow_right_alt',
  keyboard_arrow_right: 'keyboard_arrow_left', keyboard_arrow_left: 'keyboard_arrow_right',
  east: 'west', west: 'east',
  first_page: 'last_page', last_page: 'first_page',
}

/**
 * Material Symbols Rounded (loaded in index.html) — names match the PRD.
 *
 * `flip` boleh disetel false untuk ikon arah yang memang harus tetap, misalnya
 * penanda arah kiblat atau ilustrasi yang menggambarkan sesuatu yang fisik.
 */
export function Icon({ name, size = 20, fill = false, className = '', style, flip = true }) {
  const { dir } = useI18n()
  const shown = flip && dir === 'rtl' ? (MIRROR[name] || name) : name
  return (
    <span
      className={`material-symbols-rounded select-none leading-none ${className}`}
      style={{ fontSize: size, fontVariationSettings: `'FILL' ${fill ? 1 : 0}`, ...style }}
      aria-hidden="true"
    >
      {shown}
    </span>
  )
}

/* ---------------------------------------------------------------- button --- */
const btnBase = 'inline-flex items-center justify-center gap-2 font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap'
const btnSize = { sm: 'text-[13px] h-9 px-3.5', md: 'text-[15px] h-11 px-5', lg: 'text-base h-12 px-6' }
const btnKind = {
  primary: 'kx-cta-grad text-white shadow-[0_6px_16px_rgba(241,91,42,.28)] hover:brightness-[1.06] active:brightness-95',
  ink: 'bg-ink text-white hover:bg-ink-deep',
  soft: 'bg-brand-soft text-brand-deep hover:bg-[#ffe6dc]',
  outline: 'bg-white text-fg border border-line hover:border-ink/30 hover:bg-surface-sub',
  ghost: 'text-fg hover:bg-black/[.05]',
  danger: 'bg-danger text-white hover:brightness-95',
  success: 'bg-success text-white hover:brightness-95',
}

export function Button({ as: As = 'button', kind = 'primary', size = 'md', icon, iconRight, loading, className = '', children, ...rest }) {
  return (
    <As className={`${btnBase} ${btnSize[size]} ${btnKind[kind]} ${className}`} {...rest}>
      {loading ? <Spinner size={16} /> : icon ? <Icon name={icon} size={size === 'sm' ? 16 : 18} /> : null}
      {children}
      {iconRight ? <Icon name={iconRight} size={size === 'sm' ? 16 : 18} /> : null}
    </As>
  )
}

export function IconButton({ icon, label, size = 38, kind = 'outline', className = '', ...rest }) {
  return (
    <button aria-label={label} title={label} style={{ width: size, height: size }}
      className={`inline-flex items-center justify-center rounded-xl transition ${btnKind[kind]} ${className}`} {...rest}>
      <Icon name={icon} size={Math.round(size * 0.5)} />
    </button>
  )
}

/* ------------------------------------------------------------------ atoms --- */
export const Card = ({ className = '', children, ...rest }) => (
  <div className={`bg-white rounded-card shadow-card ${className}`} {...rest}>{children}</div>
)

export const Badge = ({ children, tone = 'neutral', className = '', icon }) => {
  const tones = {
    neutral: 'bg-black/[.06] text-fg', brand: 'bg-brand-soft text-brand-deep',
    ink: 'bg-ink text-white', yellow: 'bg-kx-yellow text-ink', success: 'bg-success/12 text-success',
    warn: 'bg-warn/15 text-[#9a6400]', danger: 'bg-danger/12 text-danger', gold: 'bg-gold/15 text-[#8a6a25]',
  }
  return (
    <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-[3px] text-[11px] font-extrabold uppercase tracking-wide ${tones[tone]} ${className}`}>
      {icon && <Icon name={icon} size={13} />}{children}
    </span>
  )
}

export function StatusPill({ status }) {
  const { t } = useI18n()
  const tone = {
    draft: 'neutral', pending_review: 'warn', published: 'success', rejected: 'danger', archived: 'neutral',
    active: 'success', completed: 'ink', expired: 'neutral', revoked: 'danger',
    paid: 'success', pending: 'warn', failed: 'danger', held: 'warn', payable: 'brand', processing: 'warn',
    open: 'warn', answered: 'success', scheduled: 'brand', live: 'danger', ended: 'neutral',
    confirmed: 'success', done: 'ink', cancelled: 'neutral', issued: 'success', hidden: 'neutral',
  }[status] || 'neutral'
  return <Badge tone={tone}>{t(`status.${status}`) === `status.${status}` ? status.replace(/_/g, ' ') : t(`status.${status}`)}</Badge>
}

export const ComingSoon = () => {
  const { t } = useI18n()
  return <Badge tone="yellow">{t('ui.comingSoon')}</Badge>
}

/**
 * Lambang DRRoket. Satu komponen, dipakai header, kaki halaman, sidebar admin,
 * modal masuk, dan sertifikat.
 *
 * Dulu mark ini DIGAMBAR CSS — `bg-kx-yellow` + huruf "DR" tebal — di tiga
 * berkas berbeda. Tiga salinan dari satu lambang akan menyimpang satu per satu
 * (satu dapat sudut baru, satunya tidak), dan tidak ada satu langkah pun yang
 * terlihat salah saat itu terjadi.
 *
 * Dua hal yang gampang salah waktu menukar gambar CSS jadi berkas:
 *
 * 1. **Tanpa `background`.** Berkasnya membawa persegi kuningnya sendiri dengan
 *    sudut TRANSPARAN. Menaruh `bg-kx-yellow` di belakangnya memunculkan kuning
 *    di keempat sudut — tepat di luar lengkungan gambarnya.
 * 2. **Tanpa `shadow-*`.** `box-shadow` CSS mengikuti `border-radius` elemen,
 *    bukan bentuk alfa gambarnya. Jadi `shadow-sm` di sini menjatuhkan bayangan
 *    PERSEGI di belakang lambang yang lengkung — sudutnya terlihat. Kalau suatu
 *    saat memang perlu bayangan, pakai `filter: drop-shadow()`, yang menghormati
 *    alfa.
 */
export const BrandMark = ({ size = 36, className = '' }) => (
  <img
    /* `?v=` — alasan lengkapnya di index.html. Ringkasnya: berkas di `public/`
       TIDAK di-hash Vite dan Cloudflare meng-cache `.png` 4 jam, jadi tanpa
       penanda versi ikon baru butuh 4 jam untuk terlihat — dan satu permintaan
       ke nama ini sebelum berkasnya ada menyimpan fallback SPA (`text/html`)
       di kunci itu. Naikkan angkanya setiap kali berkasnya berubah, di SINI
       dan di index.html. */
    src="/logo-drroket.png?v=1"
    /* `alt=""` — HIASAN, dan itu disengaja. Di ketiga pemakaiannya selalu ada
       teks bersebelahan yang menyebut hal yang sama: wordmark "KnoWledgeX" di
       header, kaki halaman, dan sertifikat; judul "Masuk" di modal. Memberi
       alt akan membuat pembaca layar mengumumkan satu benda dua kali. */
    alt=""
    width={size}
    height={size}
    /* 128px sumbernya, jadi tetap tajam sampai 3× pada 42px. */
    className={`shrink-0 select-none ${className}`}
    style={{ width: size, height: size }}
    draggable={false}
  />
)

export function VerifiedTick({ size = 15, className = '' }) {
  const { t } = useI18n()
  return (
    <span title={t('ui.verified')} className={`inline-flex items-center justify-center rounded-full bg-[#2E7DF6] text-white ${className}`} style={{ width: size, height: size }}>
      <Icon name="check" size={Math.round(size * 0.78)} />
    </span>
  )
}

export function Rating({ value = 0, count, size = 14, className = '' }) {
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <Icon name="star" size={size} fill className="text-star" />
      <span className="font-extrabold text-[13px]">{Number(value).toFixed(1)}</span>
      {count !== undefined && <span className="text-muted text-[12px]">({count})</span>}
    </span>
  )
}

export const ProgressBar = ({ value = 0, height = 6, tone = 'brand', className = '' }) => (
  <div className={`w-full rounded-full bg-black/[.08] overflow-hidden ${className}`} style={{ height }}>
    <div className={`h-full rounded-full transition-[width] duration-500 ${tone === 'brand' ? 'bg-brand' : tone === 'success' ? 'bg-success' : 'bg-ink'}`} style={{ width: `${Math.min(100, value)}%` }} />
  </div>
)

export function Avatar({ src, name = '', size = 40, accent = '#163A50', verified = false, className = '' }) {
  const [broken, setBroken] = useState(false)
  const ini = name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase()
  return (
    <span className={`relative inline-block shrink-0 ${className}`} style={{ width: size, height: size }}>
      {src && !broken ? (
        <img src={src} alt={name} onError={() => setBroken(true)} className="w-full h-full rounded-full object-cover bg-surface-sub" />
      ) : (
        <span className="w-full h-full rounded-full grid place-items-center font-extrabold text-white" style={{ background: accent, fontSize: size * 0.36 }}>{ini}</span>
      )}
      {/* -end-, bukan -right-: centang terpasang di sudut "akhir" nama, jadi ia
          ikut berpindah ke kiri saat halaman jadi RTL. */}
      {verified && <span className="absolute -end-0.5 -bottom-0.5"><VerifiedTick size={Math.max(13, size * 0.34)} className="ring-2 ring-white" /></span>}
    </span>
  )
}

/** Image with skeleton + graceful fallback (picsum can be slow). */
export function Thumb({ src, alt = '', ratio = '16 / 9', className = '', children, radius = 14 }) {
  const [loaded, setLoaded] = useState(false)
  const [broken, setBroken] = useState(false)
  return (
    <div className={`relative overflow-hidden bg-surface-warm ${className}`} style={{ aspectRatio: ratio, borderRadius: radius }}>
      {!loaded && !broken && <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-[#e9eef5] to-[#dfe7f0]" />}
      {broken ? (
        <div className="absolute inset-0 grid place-items-center kx-hero-grad text-white/70"><Icon name="image" size={26} /></div>
      ) : (
        <img src={src} alt={alt} loading="lazy" onLoad={() => setLoaded(true)} onError={() => { setBroken(true); setLoaded(true) }}
          className={`w-full h-full object-cover transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`} />
      )}
      {children}
    </div>
  )
}

/* ------------------------------------------------------------- feedback --- */
export const Spinner = ({ size = 18, className = '' }) => (
  <span className={`inline-block rounded-full border-2 border-current border-t-transparent animate-spin ${className}`} style={{ width: size, height: size }} />
)

export const Skeleton = ({ className = '', h, w, r = 10 }) => (
  <span className={`relative block overflow-hidden bg-black/[.07] ${className}`} style={{ height: h, width: w, borderRadius: r }}>
    <span className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/70 to-transparent" />
  </span>
)

export const CardSkeleton = () => (
  <div className="bg-white rounded-card shadow-card p-3">
    <Skeleton h={140} r={12} className="mb-3" />
    <Skeleton h={13} w="85%" className="mb-2" /><Skeleton h={13} w="60%" className="mb-4" />
    <div className="flex items-center gap-2"><Skeleton h={28} w={28} r={99} /><Skeleton h={11} w="45%" /></div>
  </div>
)

export function ErrorState({ error, onRetry, compact }) {
  const { t } = useI18n()
  return (
    <div className={`text-center ${compact ? 'py-8' : 'py-16'}`}>
      <div className="mx-auto w-12 h-12 rounded-2xl bg-danger/10 text-danger grid place-items-center mb-3"><Icon name="wifi_off" size={24} /></div>
      <p className="font-extrabold">{t('ui.error')}</p>
      <p className="text-muted text-sm mt-1 mb-4">{error?.message || '—'}{error?.code ? ` (${error.code})` : ''}</p>
      {onRetry && <Button kind="outline" size="sm" icon="refresh" onClick={onRetry}>{t('ui.retry')}</Button>}
    </div>
  )
}

export function EmptyState({ icon = 'inbox', title, hint, action }) {
  const { t } = useI18n()
  return (
    <div className="text-center py-16 px-6">
      <div className="mx-auto w-16 h-16 rounded-3xl bg-surface-warm text-ink/40 grid place-items-center mb-4"><Icon name={icon} size={30} /></div>
      <p className="font-extrabold text-lg">{title || t('ui.empty')}</p>
      <p className="text-muted text-sm mt-1 kx-pretty max-w-sm mx-auto">{hint || t('ui.emptyHint')}</p>
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  )
}

/** Wrap any useApi() result: renders skeleton / error / children(data). */
export function Async({ state, skeleton, empty, children, compact }) {
  if (state.loading && !state.data) return skeleton ?? <div className="py-16 grid place-items-center text-muted"><Spinner size={24} /></div>
  if (state.error) return <ErrorState error={state.error} onRetry={state.refetch} compact={compact} />
  const d = state.data
  const isEmpty = !d || (Array.isArray(d) && !d.length) || (d.items && !d.items.length)
  if (isEmpty && empty) return empty
  return children(d)
}

export function Toasts() {
  const { toasts } = useApp()
  return (
    <div className="fixed z-[80] bottom-5 left-1/2 -translate-x-1/2 flex flex-col gap-2 items-center pointer-events-none">
      {toasts.map((x) => (
        <div key={x.id} className={`animate-fade-up pointer-events-auto flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-bold text-white shadow-pop ${x.kind === 'error' ? 'bg-danger' : x.kind === 'info' ? 'bg-ink' : 'bg-success'}`}>
          <Icon name={x.kind === 'error' ? 'error' : x.kind === 'info' ? 'info' : 'check_circle'} size={18} fill />
          {x.message}
        </div>
      ))}
    </div>
  )
}

/* --------------------------------------------------------------- overlays --- */
export function Modal({ open, onClose, title, children, footer, width = 480 }) {
  useEffect(() => {
    if (!open) return
    const h = (e) => e.key === 'Escape' && onClose?.()
    document.addEventListener('keydown', h)
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', h); document.body.style.overflow = '' }
  }, [open, onClose])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center p-4 bg-ink-deep/55 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl2 shadow-pop w-full animate-fade-up max-h-[92vh] overflow-auto" style={{ maxWidth: width }} onClick={(e) => e.stopPropagation()}>
        {title && (
          <div className="flex items-center justify-between px-5 py-4 border-b border-line sticky top-0 bg-white rounded-t-xl2">
            <h3 className="font-extrabold text-lg">{title}</h3>
            <IconButton icon="close" label="close" kind="ghost" size={34} onClick={onClose} />
          </div>
        )}
        <div className="p-5">{children}</div>
        {footer && <div className="px-5 py-4 border-t border-line flex justify-end gap-2 sticky bottom-0 bg-white">{footer}</div>}
      </div>
    </div>
  )
}

export function Drawer({ open, onClose, title, children, footer, width = 560 }) {
  useEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [open])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-[70] flex justify-end bg-ink-deep/45 backdrop-blur-[2px]" onClick={onClose}>
      <div className="bg-white h-full w-full shadow-pop flex flex-col" style={{ maxWidth: width }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <h3 className="font-extrabold text-lg">{title}</h3>
          <IconButton icon="close" label="close" kind="ghost" size={34} onClick={onClose} />
        </div>
        <div className="flex-1 overflow-auto p-5">{children}</div>
        {footer && <div className="px-5 py-4 border-t border-line flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  )
}

/* ----------------------------------------------------------------- forms --- */
export const Label = ({ children, hint }) => (
  <span className="flex items-baseline justify-between mb-1.5">
    <span className="text-[13px] font-bold text-fg">{children}</span>
    {hint && <span className="text-[11px] text-muted">{hint}</span>}
  </span>
)

const fieldCls = 'w-full h-11 rounded-xl border border-line bg-white px-3.5 text-[15px] outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/15'

export const Input = ({ label, hint, icon, error, className = '', ...rest }) => (
  <label className={`block ${className}`}>
    {label && <Label hint={hint}>{label}</Label>}
    <span className="relative block">
      {/* start-/ps- (bukan left-/pl-) supaya ikon DAN ruang kosong untuknya
          berpindah bersamaan saat halaman jadi RTL. Kalau cuma salah satu yang
          logis, ikonnya menindih teks — dan itu hanya kelihatan di bahasa Arab. */}
      {icon && <Icon name={icon} size={18} className="absolute start-3 top-1/2 -translate-y-1/2 text-muted" />}
      <input className={`${fieldCls} ${icon ? 'ps-10' : ''} ${error ? 'border-danger' : ''}`} {...rest} />
    </span>
    {error && <span className="text-[12px] text-danger mt-1 block">{error}</span>}
  </label>
)

export const Textarea = ({ label, hint, rows = 4, className = '', ...rest }) => (
  <label className={`block ${className}`}>
    {label && <Label hint={hint}>{label}</Label>}
    <textarea rows={rows} className={`${fieldCls} h-auto py-3 leading-relaxed resize-y`} {...rest} />
  </label>
)

export const Select = ({ label, options = [], className = '', hint, ...rest }) => (
  <label className={`block ${className}`}>
    {label && <Label>{label}</Label>}
    <span className="relative block">
      <select className={`${fieldCls} appearance-none pe-9 cursor-pointer`} {...rest}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <Icon name="expand_more" size={18} className="absolute end-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
    </span>
    {hint && <span className="block text-[11px] text-muted mt-1">{hint}</span>}
  </label>
)

export const Checkbox = ({ label, checked, onChange, className = '' }) => (
  <label className={`flex items-center gap-2.5 cursor-pointer select-none ${className}`}>
    <span className={`w-[19px] h-[19px] rounded-[6px] grid place-items-center border transition ${checked ? 'bg-brand border-brand text-white' : 'border-line bg-white text-transparent'}`}>
      <Icon name="check" size={14} />
    </span>
    <input type="checkbox" className="sr-only" checked={!!checked} onChange={(e) => onChange?.(e.target.checked)} />
    <span className="text-sm">{label}</span>
  </label>
)

export const Switch = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2.5 cursor-pointer select-none">
    <span className={`w-10 h-6 rounded-full p-[3px] transition ${checked ? 'bg-success' : 'bg-black/15'}`}>
      {/* transform tidak punya padanan logis, jadi arahnya harus dibalik
          eksplisit: di RTL knob-nya mulai dari kanan, sehingga "menyala"
          berarti bergeser ke KIRI. Tanpa ini knob keluar dari relnya. */}
      <span className={`block w-[18px] h-[18px] rounded-full bg-white shadow transition-transform ${checked ? 'ltr:translate-x-4 rtl:-translate-x-4' : ''}`} />
    </span>
    <input type="checkbox" className="sr-only" checked={!!checked} onChange={(e) => onChange?.(e.target.checked)} />
    {label && <span className="text-sm font-semibold">{label}</span>}
  </label>
)

/** File-picker that uploads to /uploads and reports back the stored media URL. */
export function ImageUpload({ label, hint, value, onChange, folder = 'misc', shape = 'circle', className = '' }) {
  const { toast } = useApp()
  const inputRef = useRef(null)
  const [busy, setBusy] = useState(false)

  const onFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-selecting the same file
    if (!file) return
    if (!file.type.startsWith('image/')) { toast('File harus berupa gambar', 'error'); return }
    if (file.size > 8 * 1024 * 1024) { toast('Ukuran maksimal 8 MB', 'error'); return }
    setBusy(true)
    try {
      const { url } = await api.upload('/uploads', file, { folder })
      onChange?.(url)
      toast('Foto terunggah')
    } catch (err) {
      toast(err?.message || 'Gagal mengunggah', 'error')
    } finally { setBusy(false) }
  }

  const radius = shape === 'circle' ? 'rounded-full' : 'rounded-xl'
  return (
    <div className={`block ${className}`}>
      {label && <Label hint={hint}>{label}</Label>}
      <div className="flex items-center gap-3">
        <span className={`relative w-16 h-16 ${radius} overflow-hidden bg-surface-sub border border-line grid place-items-center shrink-0`}>
          {value ? <img src={value} alt="" className="w-full h-full object-cover" /> : <Icon name="add_a_photo" size={22} className="text-muted" />}
          {busy && <span className="absolute inset-0 grid place-items-center bg-white/70"><Spinner size={20} /></span>}
        </span>
        <div className="flex items-center gap-2">
          <Button type="button" kind="outline" size="sm" icon="upload" loading={busy} onClick={() => inputRef.current?.click()}>
            {value ? 'Ganti foto' : 'Unggah foto'}
          </Button>
          {value && !busy && <Button type="button" kind="ghost" size="sm" icon="delete" onClick={() => onChange?.('')}>Hapus</Button>}
        </div>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
      </div>
    </div>
  )
}

export function Chips({ options, value, onChange, multi = false }) {
  const active = (v) => (multi ? (value || []).includes(v) : value === v)
  const toggle = (v) => {
    if (!multi) return onChange(value === v ? '' : v)
    const set = new Set(value || [])
    set.has(v) ? set.delete(v) : set.add(v)
    onChange([...set])
  }
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={o.value} onClick={() => toggle(o.value)}
          className={`h-8 px-3 rounded-full text-[13px] font-bold border transition ${active(o.value) ? 'bg-ink text-white border-ink' : 'bg-white border-line text-fg hover:border-ink/30'}`}>
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function Tabs({ tabs, value, onChange, className = '' }) {
  return (
    <div className={`flex gap-1 border-b border-line overflow-x-auto ${className}`}>
      {tabs.map((tb) => (
        <button key={tb.value} onClick={() => onChange(tb.value)}
          className={`relative px-4 py-3 text-sm font-extrabold whitespace-nowrap transition ${value === tb.value ? 'text-brand' : 'text-muted hover:text-fg'}`}>
          {/* ms-, bukan ml-: hitungan itu mengekor label, dan di Arab "sesudah"
              berarti di sebelah kiri. Dengan ml- angkanya terlempar ke sisi
              yang salah dan terbaca seperti milik tab sebelahnya. */}
          {tb.label}{tb.count !== undefined && <span className="ms-1.5 text-[11px] font-bold opacity-70">{tb.count}</span>}
          {value === tb.value && <span className="absolute left-3 right-3 -bottom-px h-[3px] rounded-t bg-brand" />}
        </button>
      ))}
    </div>
  )
}

export function Pagination({ meta, onPage }) {
  const { t } = useI18n()
  if (!meta || meta.total_pages <= 1) return null
  const { page, total_pages: tp } = meta
  const nums = [...Array(tp)].map((_, i) => i + 1).filter((n) => n === 1 || n === tp || Math.abs(n - page) <= 1)
  return (
    <div className="flex items-center justify-center gap-1.5 py-6">
      <IconButton icon="chevron_left" label="prev" size={36} disabled={page <= 1} onClick={() => onPage(page - 1)} />
      {nums.map((n, i) => (
        <span key={n} className="flex items-center gap-1.5">
          {i > 0 && n - nums[i - 1] > 1 && <span className="text-muted px-1">…</span>}
          <button onClick={() => onPage(n)}
            className={`w-9 h-9 rounded-xl text-sm font-extrabold transition ${n === page ? 'bg-ink text-white' : 'bg-white border border-line hover:border-ink/30'}`}>{n}</button>
        </span>
      ))}
      <IconButton icon="chevron_right" label="next" size={36} disabled={page >= tp} onClick={() => onPage(page + 1)} />
      <span className="ms-2 text-[12px] text-muted hidden sm:block">{t('ui.page')} {page} {t('ui.of')} {tp}</span>
    </div>
  )
}

/* ------------------------------------------------------------- section --- */
export function SectionHead({ title, sub, action, className = '' }) {
  return (
    <div className={`flex items-end justify-between gap-4 mb-4 ${className}`}>
      <div>
        <h2 className="text-xl sm:text-[22px] font-extrabold tracking-[-0.01em]">{title}</h2>
        {sub && <p className="text-muted text-sm mt-1 kx-pretty">{sub}</p>}
      </div>
      {action}
    </div>
  )
}

/** Horizontal snap rail — used for course/expert rows on the storefront. */
export function Rail({ children, className = '' }) {
  const ref = useRef(null)
  const { dir } = useI18n()
  const rtl = dir === 'rtl'
  /**
   * `scrollBy({left})` selalu FISIK — tidak ada padanan logisnya. Di RTL, isi
   * berikutnya ada di sebelah kiri, jadi tandanya harus dibalik; kalau tidak,
   * tombol "berikutnya" justru mundur. Ikonnya ikut ditukar karena flexbox
   * sudah membalik urutan kedua tombol sendiri, sehingga tanpa penukaran ini
   * tombol di sisi kanan akan menunjuk ke kiri.
   */
  const scroll = (d) => ref.current?.scrollBy({ left: (rtl ? -d : d) * (ref.current.clientWidth * 0.8), behavior: 'smooth' })
  return (
    <div className={`relative group ${className}`}>
      <div ref={ref} className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {children}
      </div>
      {/*
        Tombol panah dulu diletakkan `-top-12 end-0` — di luar rail, naik ke
        baris judul. Masalahnya `SectionHead` menaruh `action`-nya di sudut yang
        sama persis, jadi dua komponen yang tidak saling tahu memperebutkan satu
        titik: di beranda, tautan "Konsultasi" tertimpa kedua tombol sampai
        terbaca "Kons‹ i ›". Bukan soal jarak yang kurang — selama keduanya
        mengklaim `end-0`, berapa pun jaraknya akan bentrok lagi begitu label
        aksinya lebih panjang (dan label itu diterjemahkan ke empat bahasa).

        Sekarang tombolnya tinggal DI DALAM rail, di kedua tepi. Rail memiliki
        ruangnya sendiri, `SectionHead` memiliki barisnya sendiri, dan tidak ada
        lagi sudut yang dipakai berdua.
      */}
      <div className="hidden md:block">
        {/* Nama ikon ditulis versi LTR saja — Icon yang menukarnya di RTL. */}
        <div className="absolute top-1/2 -translate-y-1/2 start-0 -ms-4 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
          <IconButton icon="chevron_left" label="scroll back" size={34} onClick={() => scroll(-1)} className="bg-white shadow-pop" />
        </div>
        <div className="absolute top-1/2 -translate-y-1/2 end-0 -me-4 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
          <IconButton icon="chevron_right" label="scroll forward" size={34} onClick={() => scroll(1)} className="bg-white shadow-pop" />
        </div>
      </div>
    </div>
  )
}
